import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import Chart from "react-apexcharts";
import { Box, Grid, Paper, Typography, Button, TextField } from '@mui/material';
import LogoutButton from "../../components/Logoutbutton";
import * as XLSX from "xlsx";
import dayjs from "dayjs";


const CO2_EMISSION_FACTOR = 0.25;
const CH4_EMISSION_FACTOR = 0.03;
const chartOptions = {
  colors: ["#8f7944", "#bf8f54", "#48594d"],
  chart: { background: "#efeee7" },
  xaxis: { labels: { style: { colors: "#1f261c" } } },
  yaxis: { labels: { style: { colors: "#1f261c" } } },
};

const Dashboard = () => {
  const [userRole, setUserRole] = useState(null);
  const [userSupplier, setUserSupplier] = useState(null);
  const [wetWeightData, setWetWeightData] = useState([]);
  const [dryWeightData, setDryWeightData] = useState([]);
  const [totalCO2, setTotalCO2] = useState(0);
  const [totalMethane, setTotalMethane] = useState(0);
  const [co2MethaneTrends, setCO2MethaneTrends] = useState([]);
  const [totalWasteCollected, setTotalWasteCollected] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserRole(userSnap.data().role);
          setUserSupplier(userSnap.data().supplier);
        }
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchShipments = async () => {
      if (!userRole) return;

      try {
        let shipmentQuery = collection(db, "shipments");
        if (userRole === "supplier" && userSupplier) {
          shipmentQuery = query(shipmentQuery, where("Collection Point", "==", userSupplier));
        }

        const querySnapshot = await getDocs(shipmentQuery);
        const shipments = querySnapshot.docs.map((doc) => doc.data());

        let wetWeightMap = {};
        let dryWeightMap = {};
        let co2TrendMap = {};
        let methaneTrendMap = {};
        let totalWaste = 0;
        let co2Total = 0;
        let methaneTotal = 0;

        shipments.forEach((item) => {
          const foodItem = item["Specific Food Item"] || item["specificFoodItem"] || "Unknown";
          const date = item["Intake Date (MM/DD/YYYY)"] || item["intakeDate"];
          const wetWeightGrams = parseFloat(item["Wet Weight (g)"] || item["wetWeight"] || 0);
          const dryWeightGrams = parseFloat(item["Dry Weight-194 (g)"] || item["dryWeight"] || 0);

          const wetWeightLbs = wetWeightGrams * 0.00220462;
          const dryWeightLbs = dryWeightGrams * 0.00220462;
          const wetWeightKg = wetWeightLbs * 0.453592;

          const co2 = wetWeightKg * CO2_EMISSION_FACTOR;
          const methane = wetWeightKg * CH4_EMISSION_FACTOR;

          if (!wetWeightMap[foodItem]) wetWeightMap[foodItem] = 0;
          if (!dryWeightMap[foodItem]) dryWeightMap[foodItem] = 0;

          wetWeightMap[foodItem] += wetWeightLbs;
          dryWeightMap[foodItem] += dryWeightLbs;
          totalWaste += wetWeightLbs;
          co2Total += co2;
          methaneTotal += methane;

          if (date) {
            if (!co2TrendMap[date]) co2TrendMap[date] = 0;
            if (!methaneTrendMap[date]) methaneTrendMap[date] = 0;
            co2TrendMap[date] += co2;
            methaneTrendMap[date] += methane;
          }
        });

        setWetWeightData(Object.keys(wetWeightMap).map(food => ({ x: food, y: wetWeightMap[food] })));
        setDryWeightData(Object.keys(dryWeightMap).map(food => ({ x: food, y: dryWeightMap[food] })));
        setTotalCO2(co2Total);
        setTotalMethane(methaneTotal);
        setTotalWasteCollected(totalWaste);

        const trendDates = Object.keys(co2TrendMap).sort();
        setCO2MethaneTrends([
          { name: "CO₂ (kg)", data: trendDates.map(date => ({ x: date, y: co2TrendMap[date] })) },
          { name: "Methane (kg)", data: trendDates.map(date => ({ x: date, y: methaneTrendMap[date] })) }
        ]);

      } catch (error) {
        console.error("Error fetching shipments:", error);
      }
    };

    fetchShipments();
  }, [userRole, userSupplier]);

  const downloadExcel = () => {
    // Prepare data
    const data = [
      ["Metric", "Value"],
      ["Total Waste Collected (lbs)", totalWasteCollected],
      ["Total CO₂ Diverted (kg)", totalCO2],
      ["Total Methane Diverted (kg)", totalMethane],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard Metrics");

    // Export to Excel
    XLSX.writeFile(workbook, "Dashboard_Metrics.xlsx");
  };

  const handleDateChange = (field, value) => {
    if (field === "startDate") setStartDate(value);
    if (field === "endDate") setEndDate(value);
  };

  const MetricCard = ({ title, value, unit }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3,
        },
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="div" color="primary">
        {typeof value === 'number' ? value.toFixed(2) : '0.00'}
        <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>
          {unit}
        </Typography>
      </Typography>
    </Paper>
  );

  // CSV download function
  const downloadCSV = () => {
    const csvData = [
      ["Metric", "Value"],
      ["Total Waste Collected", totalWasteCollected],
      ["Total CO₂ Diverted", totalCO2],
      ["Total Methane Diverted", totalMethane],
    ];

    // Create CSV content
    const csvContent = csvData.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "dashboard_metrics.csv";
    link.click();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h7" component="h1">
          {/* Conditional display based on user role */}
          {userRole === "admin" || "superadmin" ? "Admin Dashboard" : "Supplier Dashboard"}
        </Typography>
        <LogoutButton />
      </Box>

      
      {/* Metrics Cards Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Waste Collected"
            value={totalWasteCollected}
            unit="lbs"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total CO₂ Diverted"
            value={totalCO2}
            unit="kg"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Methane Diverted"
            value={totalMethane}
            unit="kg"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Total Wet Weight
            </Typography>
            <Chart
              type="bar"
              series={[{ name: "Wet Weight (lbs)", data: wetWeightData.map(d => d.y) }]}
              options={{
                ...chartOptions,
                chart: { id: "wet-weight" },
                xaxis: { categories: wetWeightData.map(d => d.x) },
                yaxis: {
                  labels: {
                    formatter: (value) => Math.round(value),
                    style: { colors: "#1f261c" }
                  }
                },
                tooltip: {
                  y: {
                    formatter: (value) => value.toFixed(2)
                  }
                },
                plotOptions: {
                  bar: {
                    borderRadius: 4,
                    horizontal: false,
                    columnWidth: '55%',
                    endingShape: 'rounded',
                  },
                },
                dataLabels: { enabled: false },
              }}
              height={300}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Total Dry Weight
            </Typography>
            <Chart
              type="bar"
              series={[{ name: "Dry Weight (lbs)", data: dryWeightData.map(d => d.y) }]}
              options={{
                ...chartOptions,
                chart: { id: "dry-weight" },
                xaxis: { categories: dryWeightData.map(d => d.x) },
                yaxis: {
                  labels: {
                    formatter: (value) => Math.round(value),
                    style: { colors: "#1f261c" }
                  }
                },
                tooltip: {
                  y: {
                    formatter: (value) => value.toFixed(2)
                  }
                },
                plotOptions: {
                  bar: {
                    borderRadius: 4,
                    horizontal: false,
                    columnWidth: '55%',
                    endingShape: 'rounded',
                  },
                },
                dataLabels: { enabled: false },
              }}
              height={300}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              CO₂ and Methane Diverted
            </Typography>
            <Chart
              type="line"
              series={co2MethaneTrends}
              options={{
                ...chartOptions,
                chart: { id: "co2-methane-trends" },
                xaxis: { categories: co2MethaneTrends[0]?.data.map((d) => d.x) },
                yaxis: {
                  labels: {
                    formatter: (value) => Math.round(value),
                    style: { colors: "#1f261c" }
                  }
                },
                tooltip: {
                  y: {
                    formatter: (value) => value.toFixed(2)
                  },
                },
              }}
              height={300}
            />

          </Paper>
        </Grid>
      </Grid>
      {/* Report Generation Section */}
      <Box sx={{ padding: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Select Date Range for Report Generation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange("startDate", e.target.value)}
            sx={{ maxWidth: 200 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange("endDate", e.target.value)}
            sx={{ maxWidth: 200 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        
        {/* Action Buttons */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              onClick={downloadExcel}
              fullWidth
              sx={{ padding: "10px", fontWeight: 'bold', backgroundColor: "#8f7944", '&:hover': { backgroundColor: "#bf8f54" } }}
            >
              Download Report as Excel
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadCSV}
              fullWidth
              sx={{ padding: "10px", fontWeight: 'bold' }}
            >
              Download Metrics as CSV
            </Button>
          </Grid>
        </Grid>
      </Box>

    </Box>
  );
};

export default Dashboard;